import { computed, inject, Service } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';

import { patchState, signalState } from '@ngrx/signals';

import {
  DashboardPinnedItemsSignalState,
  PinnedItem,
  DashboardRequestPinnedItemParams,
  DashboardPinnedItem,
} from '../../types';

import { DEFAULT_DASHBOARD_PINNED_ITEMS_SIGNAL_STATE } from '../../constants';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { concatMap, delay, pipe, switchMap, tap } from 'rxjs';

import { tapResponse } from '@ngrx/operators';
import { DashboardService } from '@data-access/dash-board/services';

export const PINNED_STUDIES_SIGNAL_STATE =
  signalState<DashboardPinnedItemsSignalState>({
    ...DEFAULT_DASHBOARD_PINNED_ITEMS_SIGNAL_STATE,
  });

@Service()
export class PinnedStudiesSitesSignalState {
  private readonly _pinnedStudiesSignalState = PINNED_STUDIES_SIGNAL_STATE;
  private readonly _dashboardService = inject(DashboardService);
  private readonly _matsnackBar = inject(MatSnackBar);

  readonly $isLoading = computed(() =>
    this._pinnedStudiesSignalState.isLoading(),
  );
  readonly $isUpdatingPinnedState = computed(() =>
    this._pinnedStudiesSignalState.isUpdatingPinnedState(),
  );
  readonly $error = computed(() => this._pinnedStudiesSignalState.error());
  readonly $data = computed<DashboardPinnedItem | null>(() =>
    this._pinnedStudiesSignalState.data(),
  );

  readonly $isPinInitiated = computed(() =>
    this._pinnedStudiesSignalState.isPinInitiated(),
  );
  readonly $lastToggledEntityId = computed<string | undefined>(() =>
    this._pinnedStudiesSignalState.lastToggledEntityId(),
  );

  /**
   * Loads the pinned studies/sites into state.
   *
   * @param requestParams - Entity type, paging, and sort parameters.
   * @param useMock - Forwarded to `DashboardService.getMyPinnedItems`; when
   * true the service serves the typed mock matching `entity_type` instead of
   * calling the API. Driven by the card component's `mockToggle` input.
   */
  getPinnedItems = rxMethod<{
    requestParams: DashboardRequestPinnedItemParams;
    useMock?: boolean;
  }>(
    pipe(
      tap(() =>
        patchState(this._pinnedStudiesSignalState, {
          isLoading: true,
          error: null,
        }),
      ),
      switchMap(({ requestParams, useMock }) => {
        return this._dashboardService
          .getMyPinnedItems(requestParams, useMock === true)
          .pipe(
            delay(250),
            tapResponse({
              error: (error: Error | null) =>
                patchState(this._pinnedStudiesSignalState, {
                  error: error,
                  isLoading: false,
                }),
              next: (data) =>
                patchState(this._pinnedStudiesSignalState, {
                  data: data,
                  isLoading: false,
                  error: null,
                }),
            }),
          );
      }),
    ),
  );

  // pinItem = rxMethod < {id: string}>()

  unpinAndPinItem = rxMethod<{
    entity_id: string;
    entity_type: 'study' | 'site';
    initiatedFromStudiesorSites?: boolean;
  }>(
    pipe(
      tap(() =>
        patchState(this._pinnedStudiesSignalState, {
          error: null,
          isUpdatingPinnedState: true,
        }),
      ),
      concatMap(({ entity_id, entity_type, initiatedFromStudiesorSites }) =>
        this._dashboardService
          .pinUnPinItem({
            id: entity_id,
            entity_type,
          })
          .pipe(
            delay(250),
            tapResponse({
              error: (error: Error) => {
                patchState(this._pinnedStudiesSignalState, {
                  error: error,
                  isUpdatingPinnedState: false,
                });
                this._matsnackBar.open(
                  'Unable to update pin. Please try again.',
                  '',
                  {
                    duration: 5000,
                    horizontalPosition: 'start',
                    panelClass: 'snackbar-error',
                  },
                );
              },
              next: (pinResponse: PinnedItem) => {
                const _pinnedItems = new Map(
                  (this._pinnedStudiesSignalState().data?.items ?? []).map(
                    (i) => [i.entity.id, i],
                  ),
                );

                if (
                  !_pinnedItems.has(pinResponse.entity.id) &&
                  pinResponse.ordinal
                ) {
                  _pinnedItems.set(pinResponse.entity.id, pinResponse);
                } else if (
                  _pinnedItems.has(pinResponse.entity.id) &&
                  !pinResponse.ordinal
                ) {
                  _pinnedItems.delete(pinResponse.entity.id);
                }
                patchState(this._pinnedStudiesSignalState, {
                  data: {
                    count: _pinnedItems.size,
                    items:
                      [..._pinnedItems.values()]?.sort(
                        (a, b) => b.ordinal - a.ordinal,
                      ) ?? [],
                  },
                  isUpdatingPinnedState: false,
                  ...(initiatedFromStudiesorSites &&
                  !this._pinnedStudiesSignalState().isPinInitiated
                    ? { isPinInitiated: true }
                    : {}),
                  lastToggledEntityId: entity_id,
                });
              },
            }),
          ),
      ),
    ),
  );

  resetSignalState() {
    patchState(this._pinnedStudiesSignalState, {
      ...DEFAULT_DASHBOARD_PINNED_ITEMS_SIGNAL_STATE,
    });
  }

  resetLastToggledEntityId() {
    patchState(this._pinnedStudiesSignalState, {
      lastToggledEntityId: undefined,
    });
  }

  displayToast(message: string) {
    this._matsnackBar.open(message, 'Close', {
      duration: 5000,
    });
  }

  loadErrorState() {
    patchState(this._pinnedStudiesSignalState, {
      isLoading: false,
      error: new Error('Error State Loading'),
    });
  }

  loadEmptyState() {
    patchState(this._pinnedStudiesSignalState, {
      isLoading: false,
      error: null,
      data: null,
    });
  }

  loadLoadingState() {
    patchState(this._pinnedStudiesSignalState, {
      isLoading: true,
      error: null,
      data: null,
    });
  }
}
